import { h, defineComponent, ref } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { create, fromEditable } from '@/services/api/resources/user';
import { filter as fetchAllRoles } from '@/services/api/resources/role';
import { filterListToOptions, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { fetchUser, getQuota, hasPermission, isOnFreePlan, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useUserCreateForm } from './useUserCreateForm';
import { useUserRecord } from './useUserRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { UpgradeAd } from '@/ui/components/upgrade/UpgradeAd';
import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
import { Userflow, UserflowTrackEvent } from '@/services/userflow/userflow';

export const UserCreatePage = defineComponent({
  name: 'UserCreatePage',
  setup() {
    if (!hasPermission(Permissions.MANAGE_USERS)) {
      return renderForbiddenPage;
    }

    const roleOptions = ref<SelectOptions>([]);

    // get record
    const getRecord = useTask(
      () => fetchAllRoles(),
      roles => {
        roleOptions.value = filterListToOptions(roles);
      },
    );

    // create empty record
    const record = useUserRecord();

    // send record
    const sendRecord = useTask(
      () => create(fromEditable(record.value)),
      async () => {
        trackEvent(EventTypes.inviteUser, {
          page: 'Create User',
          'user-email': record.value.email,
          ...EVENTS.INVITE_USER,
        });
        Userflow.trackEvent(UserflowTrackEvent.USER_INVITED);

        // update the quota for the user usage
        await fetchUser()();
        toastAndRedirect(t('user.created'), 'administration');
      },
    );

    // form
    const { formFields, handleSubmit } = useUserCreateForm(record, sendRecord);

    return function render() {
      const upgradeMessage = h(UpgradeAd, {
        class: 'upgrade-ad--create-user',
        props: {
          message: t('upgrade-ad.user-create-quota', { quota: getQuota('user').toString() }),
          isVertical: true,
        },
      });

      return container(
        [
          pageTitle(t('user.create')),
          box([
            loader('form', getRecord, [
              isOnFreePlan.value ? upgradeMessage : null,
              ...formFields(roleOptions.value),
              loadingButton(
                t('user.create'),
                handleSubmit,
                sendRecord.state.value.pending,
              ),
            ]),
          ]),
        ],
        'narrow',
      );
    };
  },
});
